import { Checkbox } from "@/components/ui/checkbox";
import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { utils, write } from "xlsx";
import { Copy, Search, X, ChevronDown, ChevronRight } from "lucide-react";
import {
  listAdminCustomers,
  getAdminCustomer,
  listAdminCustomerOrders,
  CUSTOMERS_PAGE_SIZE,
} from "@/lib/admin/customers.functions";
import {
  updateAdminCustomer,
  setCustomerStatus,
  deleteAdminCustomer,
} from "@/lib/admin/customers-crud.functions";
import { listCustomerRefunds } from "@/lib/admin/customer-refunds.functions";
import {
  getAdminCustomerOrderHistory,
  getAdminOrderTimeline,
  type AdminCustomerOrderHistoryRow,
} from "@/lib/admin/wallet.functions";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ImportButtons } from "@/components/admin/import/ImportButtons";
import { Pager } from "@/components/admin/Pager";
import {
  OrderStatusBadge,
  PaymentStatusBadge,
} from "@/components/customer/OrderStatusBadge";
import { formatPaise } from "@/lib/format/money";
import { formatISTDate, formatISTDateTime } from "@/lib/date/ist";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_admin/admin/customers")({
  component: AdminCustomersPage,
});

type StatusFilter = "active" | "inactive" | "all";

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-7 gap-1 px-2 text-xs"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(value);
          toast.success(`${label} copied`);
        } catch {
          toast.error(`Couldn't copy ${label.toLowerCase()}`);
        }
      }}
    >
      <Copy className="h-3 w-3" aria-hidden />
      Copy
    </Button>
  );
}

function Field({
  label,
  value,
  copyLabel,
}: {
  label: string;
  value: string | null | undefined;
  copyLabel?: string;
}) {
  const shown = value && String(value).trim().length > 0 ? String(value) : "—";
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border py-2 last:border-0">
      <span className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="flex items-center gap-1 text-right text-sm text-foreground">
        <span className="break-all">{shown}</span>
        {copyLabel && shown !== "—" ? (
          <CopyButton value={shown} label={copyLabel} />
        ) : null}
      </span>
    </div>
  );
}

function AdminCustomersPage() {
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<StatusFilter>("active");
  const [page, setPage] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Lifting state for customer actions
  const [actionCustomerId, setActionCustomerId] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [deactivating, setDeactivating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [editForm, setEditForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    locale: "",
    marketing_opt_in: false,
  });
  const [originalOptIn, setOriginalOptIn] = useState<boolean | null>(null);
  const [editLoading, setEditLoading] = useState(false);

  const queryClient = useQueryClient();
  const list = useServerFn(listAdminCustomers);
  const deleteCustomer = useServerFn(deleteAdminCustomer);
  const updateStatus = useServerFn(setCustomerStatus);
  const updateCustomer = useServerFn(updateAdminCustomer);
  const getCustomerForEdit = useServerFn(getAdminCustomer);

  const openEdit = async (row: { customer_id: string; full_name: string | null; email: string | null; phone: string | null }) => {
    setActionCustomerId(row.customer_id);
    setEditForm({ full_name: row.full_name ?? "", email: row.email ?? "", phone: row.phone ?? "", locale: "", marketing_opt_in: false });
    setOriginalOptIn(null);
    setEditMode(true);
    setEditLoading(true);
    try {
      const d = await getCustomerForEdit({ data: { customerId: row.customer_id } });
      const p = d?.profile;
      if (p) {
        setEditForm({
          full_name: p.full_name ?? "",
          email: p.email ?? "",
          phone: p.phone ?? "",
          locale: p.locale ?? "",
          marketing_opt_in: p.marketing_opt_in === true,
        });
        setOriginalOptIn(p.marketing_opt_in === true);
      }
    } catch {
      toast.error("Could not load the full customer profile. Marketing opt-in cannot be changed right now.");
    } finally {
      setEditLoading(false);
    }
  };

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin", "customers", search, status, page],
    queryFn: () => list({ data: { search: search || undefined, status, page } }),
  });

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!actionCustomerId) return;
    try {
      await updateCustomer({
        data: {
          customerId: actionCustomerId,
          full_name: editForm.full_name,
          email: editForm.email,
          phone: editForm.phone || null,
          locale: editForm.locale || null,
          // Consent only sent when the admin explicitly changed it.
          ...(originalOptIn !== null && editForm.marketing_opt_in !== originalOptIn
            ? { marketing_opt_in: editForm.marketing_opt_in }
            : {}),
        },
      });
      toast.success("Customer updated");
      setEditMode(false);
      setActionCustomerId(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "customer", actionCustomerId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "customers"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update");
    }
  };

  const handleToggleStatus = async () => {
    if (!actionCustomerId) return;
    // Find customer in data to get current status
    const customer = data?.rows.find(r => r.customer_id === actionCustomerId);
    if (!customer) return;

    const newStatus = customer.status === "active" ? "inactive" : "active";
    try {
      await updateStatus({
        data: {
          customerId: actionCustomerId,
          status: newStatus as "active" | "inactive",
        },
      });
      toast.success(`Customer ${newStatus}d`);
      setDeactivating(false);
      setActionCustomerId(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "customer", actionCustomerId] });
      queryClient.invalidateQueries({ queryKey: ["admin", "customers"] });
    } catch (err: any) {
      toast.error(err.message || "Failed to update status");
      setDeactivating(false);
    }
  };

  const handleDelete = async () => {
    if (!actionCustomerId) return;
    try {
      await deleteCustomer({ data: { customerId: actionCustomerId } });
      toast.success("Customer deleted successfully.");
      setDeleting(false);
      setActionCustomerId(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "customers"] });
    } catch (err: any) {
      // Catch business rule errors or generic failures to prevent blank screen
      const message = err.message || "Failed to delete";
      toast.error(message);
      setDeleting(false);
    }
  };


  return (
    <div>
      <AdminPageHeader
        title="Customers"
        description="Search by name, email, phone digits or order reference."
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (!data) return;
                const rows = data.rows.map((r) => [
                  r.display_name ?? "",
                  r.full_name ?? "",
                  r.email ?? "",
                  r.phone ?? "",
                  r.status ?? "",
                  r.order_count ?? 0,
                  r.first_order_at ? formatISTDate(r.first_order_at) : "—",
                  r.last_order_at ? formatISTDate(r.last_order_at) : "—",
                  formatISTDate(r.created_at),
                  "N/A", // marketing_opt_in is detail-only
                ]);
                const header = [
                  "Display Name",
                  "Full Name",
                  "Email",
                  "Phone",
                  "Status",
                  "Order Count",
                  "First Order",
                  "Last Order",
                  "Customer Since",
                  "Marketing Opt-in",
                ];
                downloadCsv("customers-export.csv", toCsv([header, ...rows]));
              }}
            >
              Export Customers
            </Button>
            <ImportButtons moduleId="customers" />
          </div>
        }
      />

      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center"
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(searchInput.trim());
        }}
      >
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Name, email, phone or order reference"
            aria-label="Search customers"
            className="pl-9"
          />
        </div>
        <Select
          value={status}
          onValueChange={(v) => {
            setStatus(v as StatusFilter);
            setPage(1);
          }}
        >
          <SelectTrigger className="sm:w-44" aria-label="Status filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active only</SelectItem>
            <SelectItem value="inactive">Inactive only</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button type="submit">Search</Button>
          {search ? (
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setSearchInput("");
                setSearch("");
                setPage(1);
              }}
              className="gap-1"
            >
              <X className="h-4 w-4" aria-hidden />
              Clear
            </Button>
          ) : null}
        </div>
      </form>

      {isError ? (
        <Alert variant="destructive" className="mb-4">
          <AlertDescription>
            Couldn't load customers. Please refresh and try again.
          </AlertDescription>
        </Alert>
      ) : null}

      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : data && data.rows.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Orders</TableHead>
                  <TableHead>First order</TableHead>
                  <TableHead>Last order</TableHead>
                  <TableHead>Customer since</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.rows.map((row) => (
                  <TableRow key={row.customer_id}>
                    <TableCell className="font-medium">
                      {row.display_name}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      <div className="break-all">{row.email ?? "—"}</div>
                      <div>{row.phone ?? "—"}</div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          row.status === "active" ? "default" : row.status === "guest" ? "secondary" : "outline"
                        }
                      >
                        {row.status === "active" ? "Active" : row.status === "guest" ? "Guest" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.order_count}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.first_order_at ? formatISTDate(row.first_order_at) : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.last_order_at ? formatISTDate(row.last_order_at) : "—"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatISTDate(row.created_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      {row.status === "guest" ? (
                        <div className="flex justify-end">
                          <Button asChild variant="outline" size="sm">
                            <Link to="/admin/orders">Open in Orders</Link>
                          </Button>
                        </div>
                      ) : (
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedId(row.customer_id)}
                        >
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => void openEdit(row)}
                        >
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setActionCustomerId(row.customer_id);
                            setDeactivating(true);
                          }}
                        >
                          {row.status === "active" ? "Deactivate" : "Activate"}
                        </Button>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            setActionCustomerId(row.customer_id);
                            setDeleting(true);
                          }}
                        >
                          Delete
                        </Button>
                      </div>
                      )}
                    </TableCell>

                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pager
            page={data.page}
            pageSize={data.pageSize}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No customers match this search.
        </p>
      )}

      <Dialog open={editMode} onOpenChange={(open) => {
        if (!open) {
          setEditMode(false);
          setActionCustomerId(null);
        }
      }}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Customer</DialogTitle>
            <DialogDescription>
              Update the customer's profile information.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdate} className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit_full_name">Full Name</Label>
              <Input
                id="edit_full_name"
                value={editForm.full_name}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, full_name: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_email">Email</Label>
              <Input
                id="edit_email"
                type="email"
                value={editForm.email}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, email: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_phone">Phone</Label>
              <Input
                id="edit_phone"
                value={editForm.phone}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, phone: e.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit_locale">Locale</Label>
              <Input
                id="edit_locale"
                placeholder="e.g. en-IN"
                value={editForm.locale}
                disabled={editLoading}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, locale: e.target.value }))
                }
              />
            </div>
            <div className="flex items-start gap-3 rounded-md border border-border p-3">
              <Checkbox
                id="edit_marketing_opt_in"
                checked={editForm.marketing_opt_in}
                disabled={editLoading || originalOptIn === null}
                onCheckedChange={(v) =>
                  setEditForm((prev) => ({ ...prev, marketing_opt_in: v === true }))
                }
              />
              <div className="space-y-1">
                <Label htmlFor="edit_marketing_opt_in">Marketing opt-in</Label>
                <p className="text-xs text-muted-foreground">
                  {editLoading
                    ? "Loading current consent…"
                    : `Currently: ${editForm.marketing_opt_in ? "Yes" : "No"}. Only tick this if the customer has explicitly agreed to receive marketing emails.`}
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditMode(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={editLoading}>Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deactivating} onOpenChange={(open) => {
        if (!open) {
          setDeactivating(false);
          setActionCustomerId(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {data?.rows.find(r => r.customer_id === actionCustomerId)?.status === "active" ? "Deactivate" : "Activate"} Customer
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to{" "}
              {data?.rows.find(r => r.customer_id === actionCustomerId)?.status === "active" ? "deactivate" : "activate"} this
              customer?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleToggleStatus}>Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={deleting} onOpenChange={(open) => {
        if (!open) {
          setDeleting(false);
          setActionCustomerId(null);
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Customer</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this customer? This
              action cannot be undone and is only allowed if the customer has
              zero orders.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <CustomerDetailSheet
        customerId={selectedId}
        onClose={() => setSelectedId(null)}
      />

    </div>
  );
}

function CustomerDetailSheet({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const [ordersPage, setOrdersPage] = useState(1);
  const getCustomer = useServerFn(getAdminCustomer);
  const listOrders = useServerFn(listAdminCustomerOrders);
  const listRefunds = useServerFn(listCustomerRefunds);

  const detail = useQuery({
    queryKey: ["admin", "customer", customerId],
    enabled: !!customerId,
    queryFn: () => getCustomer({ data: { customerId: customerId! } }),
  });

  const orders = useQuery({
    queryKey: ["admin", "customer-orders", customerId, ordersPage],
    enabled: !!customerId,
    queryFn: () =>
      listOrders({ data: { customerId: customerId!, page: ordersPage } }),
  });

  const refunds = useQuery({
    queryKey: ["admin", "customer-refunds", customerId],
    enabled: !!customerId,
    queryFn: () => listRefunds({ data: { customerId: customerId! } }),
  });

  const d = detail.data;


  return (
    <Sheet
      open={!!customerId}
      onOpenChange={(open) => {
        if (!open) {
          setOrdersPage(1);
          onClose();
        }
      }}
    >
      <SheetContent className="w-full overflow-y-auto sm:max-w-2xl">
        <SheetHeader>
          <SheetTitle>{d?.profile.display_name ?? "Customer"}</SheetTitle>
          <SheetDescription>
            Read-only customer record. Editing, roles and passwords are not
            managed here.
          </SheetDescription>
        </SheetHeader>

        {detail.isLoading ? (
          <div className="mt-4 space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : detail.isError ? (
          <Alert variant="destructive" className="mt-4">
            <AlertDescription>
              Couldn't load this customer. Please try again.
            </AlertDescription>
          </Alert>
        ) : !d ? (
          <p className="mt-6 text-sm text-muted-foreground">
            This customer no longer exists.
          </p>
        ) : (
          <Tabs defaultValue="overview" className="mt-4">
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="refunds">Refunds</TabsTrigger>
              <TabsTrigger value="order-history">Order History</TabsTrigger>
            </TabsList>
            <TabsContent value="overview">
          <div className="mt-4 space-y-8">
            <section>
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <h3 className="text-sm font-semibold text-foreground">Profile</h3>
                <Badge variant="secondary">
                  Customer since {formatISTDate(d.profile.created_at)}
                </Badge>
                <Badge
                  variant={d.profile.status === "active" ? "default" : "outline"}
                >
                  {d.profile.status === "active" ? "Active" : "Inactive"}
                </Badge>
              </div>
              <div className="rounded-md border border-border px-3">
                <Field label="Display name" value={d.profile.display_name} />
                <Field label="Full name" value={d.profile.full_name} />
                <Field label="Email" value={d.profile.email} copyLabel="Email" />
                <Field
                  label="Email verified"
                  value={
                    d.profile.email_verified_at
                      ? formatISTDateTime(d.profile.email_verified_at)
                      : "Not verified"
                  }
                />
                <Field label="Phone" value={d.profile.phone} copyLabel="Phone" />
                <Field
                  label="Phone verified"
                  value={
                    d.profile.phone_verified_at
                      ? formatISTDateTime(d.profile.phone_verified_at)
                      : "Not verified"
                  }
                />
                <Field label="Locale" value={d.profile.locale} />
                <Field
                  label="Marketing opt-in"
                  value={d.profile.marketing_opt_in ? "Yes" : "No"}
                />
                <Field
                  label="Customer ID"
                  value={d.profile.customer_id}
                  copyLabel="Customer ID"
                />
                <Field
                  label="Auth user ID"
                  value={d.profile.auth_user_id}
                  copyLabel="Auth user ID"
                />
              </div>

            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                Saved addresses
              </h3>
              {d.addresses.length === 0 ? (
                <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No saved addresses. Delivery addresses captured on individual
                  orders are shown on the order, not here.
                </p>
              ) : (
                <ul className="space-y-3">
                  {d.addresses.map((a) => (
                    <li
                      key={a.id}
                      className="rounded-md border border-border p-3 text-sm"
                    >
                      <div className="mb-1 flex flex-wrap items-center gap-2">
                        <span className="font-medium">{a.label ?? "Address"}</span>
                        {a.is_default ? <Badge>Default</Badge> : null}
                        {a.is_archived ? (
                          <Badge variant="outline">Archived</Badge>
                        ) : null}
                      </div>
                      <p className="text-muted-foreground">
                        {[
                          a.contact_name,
                          a.contact_phone,
                          a.address_line1,
                          a.address_line2,
                          a.landmark,
                          a.delivery_area_name,
                          a.city,
                          a.state,
                          a.pincode,
                        ]
                          .filter(
                            (v) => v && String(v).trim().length > 0,
                          )
                          .join(", ")}
                      </p>
                      {a.delivery_notes ? (
                        <p className="mt-1 text-xs text-muted-foreground">
                          Notes: {a.delivery_notes}
                        </p>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                Lifetime stats
              </h3>
              <div className="rounded-md border border-border px-3">
                <Field
                  label="Orders"
                  value={String(d.stats.order_count ?? 0)}
                />
                <Field
                  label="First order"
                  value={
                    d.stats.first_order_at
                      ? formatISTDateTime(d.stats.first_order_at)
                      : "—"
                  }
                />
                <Field
                  label="Last order"
                  value={
                    d.stats.last_order_at
                      ? formatISTDateTime(d.stats.last_order_at)
                      : "—"
                  }
                />
                <Field
                  label="Lifetime paid"
                  value={formatPaise(d.stats.lifetime_paid_paise)}
                />
              </div>
            </section>

            <section>
              <h3 className="mb-2 text-sm font-semibold text-foreground">
                Orders
              </h3>
              {orders.isLoading ? (
                <Skeleton className="h-24 w-full" />
              ) : orders.isError ? (
                <Alert variant="destructive">
                  <AlertDescription>
                    Couldn't load this customer's orders.
                  </AlertDescription>
                </Alert>
              ) : orders.data && orders.data.rows.length > 0 ? (
                <>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Reference</TableHead>
                          <TableHead>Placed</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Payment</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead />
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.data.rows.map((o) => (
                          <TableRow key={o.order_id}>
                            <TableCell className="font-mono text-xs">
                              <span className="flex items-center gap-1">
                                {o.order_ref}
                                <CopyButton
                                  value={o.order_ref}
                                  label="Order reference"
                                />
                              </span>
                            </TableCell>
                            <TableCell className="text-xs">
                              {formatISTDateTime(o.created_at)}
                            </TableCell>
                            <TableCell>
                              <OrderStatusBadge status={o.status} />
                            </TableCell>
                            <TableCell>
                              <PaymentStatusBadge status={o.payment_status} />
                            </TableCell>
                            <TableCell className="text-right tabular-nums">
                              {formatPaise(o.total_amount_paise)}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                asChild
                                variant="ghost"
                                size="sm"
                                className="text-xs"
                              >
                                <Link to="/admin/orders">Open in Orders</Link>
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                  {orders.data.total > CUSTOMERS_PAGE_SIZE ? (
                    <Pager
                      page={orders.data.page}
                      pageSize={orders.data.pageSize}
                      total={orders.data.total}
                      onPageChange={setOrdersPage}
                    />
                  ) : null}
                </>
              ) : (
                <p className="rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No orders linked to this customer yet.
                </p>
              )}
            </section>
          </div>
            </TabsContent>
            <TabsContent value="refunds">
              <CustomerRefundsTab customerId={customerId!} />
            </TabsContent>
            <TabsContent value="order-history">
              <CustomerOrderHistoryTab customerId={customerId!} />
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>

    </Sheet>
  );
}

function CustomerRefundsTab({ customerId }: { customerId: string }) {
  const listRefunds = useServerFn(listCustomerRefunds);
  const { data: refunds, isLoading } = useQuery({
    queryKey: ["admin", "customer-refunds", customerId],
    queryFn: () => listRefunds({ data: { customerId } }),
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;

  if (!refunds || refunds.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-8 text-center">
        <p className="text-sm text-muted-foreground">No refunds found for this customer.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Order Ref</TableHead>
              <TableHead>Payment Ref</TableHead>
              <TableHead>Amount</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Gateway</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {refunds.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.order_ref}</TableCell>
                <TableCell className="font-mono text-xs">{r.gateway_refund_id || "—"}</TableCell>
                <TableCell className="tabular-nums">{formatPaise(r.amount_paise)}</TableCell>
                <TableCell>
                  <Badge variant={r.status === "processed" ? "default" : "secondary"}>
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{formatISTDateTime(r.refunded_at)}</TableCell>
                <TableCell className="text-xs uppercase">{r.method || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
function toCsv(rows: (string | number)[][]): string {
  return rows
    .map((r) =>
      r
        .map((cell) => {
          const s = String(cell ?? "");
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
}

function downloadCsv(filename: string, csv: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function downloadXlsx(filename: string, rows: (string | number)[][]) {
  const ws = utils.aoa_to_sheet(rows);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, "Sheet1");
  const buf = write(wb, { type: "array", bookType: "xlsx" }) as ArrayBuffer;
  const blob = new Blob([buf], { type: "application/octet-stream" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function CustomerWalletTab({ customerId }: { customerId: string }) {
  return (
    <div className="mt-8 rounded-md border border-dashed border-border p-8 text-center">
      <p className="text-sm text-muted-foreground">Wallet management has been moved to the Operations portal.</p>
    </div>
  );
}

const ORDER_HISTORY_PAGE_SIZE = 15;

function OrderTimeline({ orderId }: { orderId: string }) {
  const getTimelineFn = useServerFn(getAdminOrderTimeline);
  const timeline = useQuery({
    queryKey: ["admin", "order-timeline", orderId],
    queryFn: () => getTimelineFn({ data: { orderId } }),
  });

  if (timeline.isLoading) return <Skeleton className="h-16 w-full" />;
  if (timeline.isError)
    return (
      <p className="text-xs text-destructive">Couldn't load the timeline.</p>
    );
  if (!timeline.data || timeline.data.length === 0)
    return <p className="text-xs text-muted-foreground">No timeline events.</p>;

  return (
    <ol className="space-y-2 border-l border-border pl-4">
      {timeline.data.map((e, i) => (
        <li key={i} className="text-xs">
          <p className="font-medium text-foreground">{e.stage}</p>
          <p className="text-muted-foreground">{formatISTDateTime(e.at)}{e.actor ? ` · ${e.actor}` : ""}</p>
          {e.note ? <p className="text-muted-foreground">{e.note}</p> : null}
        </li>
      ))}
    </ol>
  );
}

function CustomerOrderHistoryTab({ customerId }: { customerId: string }) {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<string | null>(null);

  const listFn = useServerFn(getAdminCustomerOrderHistory);
  const orders = useQuery({
    queryKey: ["admin", "customer-order-history", customerId, search, status, page],
    queryFn: () =>
      listFn({
        data: {
          customerId,
          search: search || undefined,
          status,
          limit: ORDER_HISTORY_PAGE_SIZE,
          offset: (page - 1) * ORDER_HISTORY_PAGE_SIZE,
        },
      }),
    placeholderData: (prev) => prev,
  });


  const exportRows = useMemo(() => {
    const rows: (string | number)[][] = [
      [
        "Order Number",
        "Order Date",
        "Delivery Date",
        "Items",
        "Meal Plan",
        "Order Value",
        "Payment Method",
        "Wallet Used",
        "Refund Amount",
        "Refund Destination",
        "Status",
      ],
      ...(orders.data?.rows ?? []).map((o) => [
        o.order_ref,
        o.created_at,
        o.delivery_date ?? "",
        o.items_summary ?? "",
        o.meal_plan_title ?? "",
        (o.total_amount_paise / 100).toFixed(2),
        o.payment_method ?? "",
        ((o.wallet_used_paise ?? 0) / 100).toFixed(2),
        ((o.refund_paise ?? 0) / 100).toFixed(2),
        o.refund_destination ?? "",
        o.status,
      ]),
    ];
    return rows;
  }, [orders.data]);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            setSearch(searchInput.trim());
          }}
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Order reference or item"
              className="w-56 pl-9"
            />
          </div>
          <Select
            value={status}
            onValueChange={(v) => {
              setStatus(v);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="confirmed">Confirmed</SelectItem>
              <SelectItem value="delivered">Delivered</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
              <SelectItem value="refunded">Refunded</SelectItem>
            </SelectContent>
          </Select>
          <Button type="submit">Search</Button>
        </form>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!orders.data}
            onClick={() => downloadCsv(`order-history-${customerId}.csv`, toCsv(exportRows))}
          >
            Export CSV
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!orders.data}
            onClick={() => downloadXlsx(`order-history-${customerId}.xlsx`, exportRows)}
          >
            Export Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            Print
          </Button>
        </div>
      </div>

      {orders.isError ? (
        <Alert variant="destructive">
          <AlertDescription>Couldn't load order history.</AlertDescription>
        </Alert>
      ) : orders.isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : orders.data && orders.data.rows.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead />
                  <TableHead>Order</TableHead>
                  <TableHead>Order date</TableHead>
                  <TableHead>Delivery date</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Meal plan</TableHead>
                  <TableHead className="text-right">Value</TableHead>
                  <TableHead>Payment</TableHead>
                  <TableHead className="text-right">Wallet used</TableHead>
                  <TableHead className="text-right">Refund</TableHead>
                  <TableHead>Refund to</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {orders.data.rows.map((o) => (
                  <>
                    <TableRow key={o.order_id}>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setExpanded(expanded === o.order_id ? null : o.order_id)}
                        >
                          {expanded === o.order_id ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </Button>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{o.order_ref}</TableCell>
                      <TableCell className="text-xs">{formatISTDateTime(o.created_at)}</TableCell>
                      <TableCell className="text-xs">
                        {o.delivery_date ? formatISTDate(o.delivery_date) : "—"}
                      </TableCell>
                      <TableCell className="max-w-[160px] truncate text-xs" title={o.items_summary ?? ""}>
                        {o.items_summary ?? "—"}
                      </TableCell>
                      <TableCell className="text-xs">{o.meal_plan_title ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(o.total_amount_paise)}
                      </TableCell>
                      <TableCell className="text-xs">{o.payment_method ?? "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(o.wallet_used_paise ?? 0)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatPaise(o.refund_paise ?? 0)}
                      </TableCell>
                      <TableCell className="text-xs">{o.refund_destination ?? "—"}</TableCell>
                      <TableCell>
                        <OrderStatusBadge status={o.status} />
                      </TableCell>
                      <TableCell className="text-right">
                        {/* Refund to wallet functionality removed as per scope requirements. Refunds are now read-only in the Refunds tab. */}
                      </TableCell>
                    </TableRow>
                    {expanded === o.order_id ? (
                      <TableRow key={`${o.order_id}-timeline`}>
                        <TableCell colSpan={13} className="bg-muted/30">
                          <div className="p-3">
                            <p className="mb-2 text-xs font-semibold text-foreground">Timeline</p>
                            <OrderTimeline orderId={o.order_id} />
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </>
                ))}
              </TableBody>
            </Table>
          </div>
          <Pager
            page={page}
            pageSize={ORDER_HISTORY_PAGE_SIZE}
            total={orders.data.total}
            onPageChange={setPage}
          />
        </>
      ) : (
        <p className="rounded-md border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
          No orders match this search.
        </p>
      )}

      {/* Wallet refund dialog removed as per scope requirements. Refunds are now read-only in the Refunds tab. */}
    </div>
  );
}
